import * as React from "react";

import { ButtonGrid } from "@fluentui/react";
import { ITheme } from "@fluentui/react/lib/Styling";
import { Async, KeyCodes } from "@fluentui/react/lib/Utilities";

import { dataUniqueId, getInputMethodFromEvent } from "@1js/acui-common";
import { AppThemeConsumer } from "@1js/acui-theme";

import {
  getClassNames,
  InsertTableClassNames,
} from "./RibbonInsertTable.classNames";
import {
  InsertTableCell,
  RibbonInsertTableProps,
} from "./RibbonInsertTable.Props";
import { getInsertTableStyles } from "./RibbonInsertTable.styles";

export interface InsertTableState {
  hoveredCellIndex?: number;
  displayTitle?: string;
}
export class RibbonInsertTable extends React.PureComponent<
  RibbonInsertTableProps,
  InsertTableState
> {
  private navigationIdleTimeoutId: number | undefined;
  private isNavigationIdle: boolean;
  private readonly navigationIdleDelay_ms: number = 250;
  private async: Async;

  constructor(props: RibbonInsertTableProps) {
    super(props);

    this.isNavigationIdle = true;
    this.async = new Async(this);

    this.state = {
      hoveredCellIndex: undefined,
      displayTitle: this.props.title,
    };
  }

  public componentWillUnmount() {
    this.async.dispose();
  }

  get cellCount() {
    return this.props.columns * this.props.rows;
  }

  public render() {
    return (
      <AppThemeConsumer>
        {(theme: ITheme) => {
          const styles = getInsertTableStyles(
            theme,
            this.props.appColors,
            this.props.styles
          );
          const classNames = getClassNames(
            styles,
            false,
            !!this.props.disabled
          );
          return (
            <div>
              {this.state.displayTitle && this.renderTableTitle(classNames)}
              {this.renderTable(classNames)}
            </div>
          );
        }}
      </AppThemeConsumer>
    );
  }

  private renderTableTitle(classNames: InsertTableClassNames): JSX.Element {
    return (
      <div
        id={this.props.id}
        className={classNames.tableTitle}
        role="heading"
        aria-level={1}
      >
        <span className={classNames.tableTitleInnerSpan}>
          {this.state.displayTitle}
        </span>
      </div>
    );
  }

  private renderTable(classNames: InsertTableClassNames): JSX.Element {
    return (
      <div
        className={classNames.root}
        aria-labelledby={this.props.id}
        onFocus={this.props.onFocus}
        data-automation-type="RibbonInsertTable"
        data-unique-id={dataUniqueId(this.props)}
        aria-posinset={this.props.positionInSet}
        aria-setsize={this.props.setSize}
        data-no-vertical-wrap={true}
        data-no-horizontal-wrap={false}
      >
        <ButtonGrid
          items={this.generateItems(this.cellCount)}
          columnCount={this.props.columns}
          onRenderItem={this.onRenderItem}
          doNotContainWithinFocusZone={true}
          aria-labelledby={this.props.id}
        />
      </div>
    );
  }

  private onMouseLeave = (
    ev: React.MouseEvent<HTMLButtonElement | HTMLDivElement>
  ): void => {
    if (!this.isNavigationIdle || this.props.disabled) {
      return;
    }
    // Get the parent menu of the target and focus it
    const elements = document.querySelectorAll('[tabindex="0"][role="menu"]');

    for (let index = 0; index < elements.length; index += 1) {
      if (elements[index].contains(ev.currentTarget)) {
        /**
         * IE11 focus() method forces parents to scroll to top of element.
         * Edge and IE expose a setActive() function for focusable divs that
         * sets the page focus but does not scroll the parent element.
         */
        if ((elements[index] as any).setActive) {
          (elements[index] as any).setActive();
        } else {
          (elements[index] as HTMLElement).focus();
        }

        break;
      }
    }

    this.setState({
      hoveredCellIndex: undefined,
    });
    if (this.props.onPreviewTitle) {
      this.setState({
        displayTitle: this.buildPreviewTitle(
          undefined /* column */,
          undefined /* row */
        ),
      });
    }
  };

  private onKeydown = (
    item: InsertTableCell,
    event: React.KeyboardEvent<HTMLDivElement>
  ) => {
    if (this.props.disabled) {
      return;
    }

    if (
      event.which === KeyCodes.up ||
      event.which === KeyCodes.down ||
      event.which === KeyCodes.left ||
      event.which === KeyCodes.right
    ) {
      const newHoveredCellIndex = indexForKeyboardEvent(
        event.keyCode,
        this.state.hoveredCellIndex,
        this.props.columns,
        this.cellCount
      );

      event.preventDefault();
      event.stopPropagation();
      this.setState({
        hoveredCellIndex: newHoveredCellIndex,
      });
      if (this.props.title && this.props.onPreviewTitle) {
        this.updatePreviewTitle(
          newHoveredCellIndex,
          this.props.columns,
          this.cellCount,
          true /* updateDisplayTitle */
        );
        const newLabel = this.updatePreviewTitle(
          newHoveredCellIndex,
          this.props.columns,
          this.cellCount,
          true /* updateDisplayTitle */
        );
        const newElementToFocus = document.querySelector(
          `[aria-label="${newLabel}"]`
        );
        newElementToFocus && (newElementToFocus as HTMLElement).focus();
      }

      this.setNavigationTimeout();
    } else if (
      event.which === KeyCodes.enter &&
      this.state.hoveredCellIndex !== undefined
    ) {
      event.preventDefault();
      event.stopPropagation();
      this.onClick(item, event);
    }
  };

  private updatePreviewTitle(
    index: number,
    columnCount: number,
    cellCount: number,
    updateDisplayTitle: boolean
  ): string | undefined {
    let column = columnIndexForCellAtIndex(index, columnCount, cellCount);
    let row = rowIndexForCellAtIndex(index, columnCount, cellCount);

    column = column !== undefined ? column + 1 : undefined;
    row = row !== undefined ? row + 1 : undefined;

    const title = this.buildPreviewTitle(column, row);

    if (updateDisplayTitle) {
      this.setState({
        displayTitle: title,
      });
    }

    return title;
  }

  private buildPreviewTitle(
    column: number | undefined,
    row: number | undefined
  ): string | undefined {
    if (
      column !== undefined &&
      row !== undefined &&
      this.props.onPreviewTitle
    ) {
      return this.props.onPreviewTitle
        .replace("{0}", String(column))
        .replace("{1}", String(row));
    }

    return this.props.title;
  }

  private onRenderItem = (item: InsertTableCell): JSX.Element => {
    return (
      <AppThemeConsumer>
        {(theme: ITheme) => {
          const styles = getInsertTableStyles(
            theme,
            this.props.appColors,
            this.props.styles
          );
          const classNames = getClassNames(
            styles,
            this.isCellHighlighted(item.index),
            !!this.props.disabled
          );
          // TODO: Replace anonymous functions callbacks when Grid is refactored (1782311)
          return (
            <div
              role={"gridcell"}
              aria-label={item.label}
              className={classNames.cell}
              // tslint:disable-next-line:jsx-no-lambda
              onMouseEnter={(ev: React.MouseEvent<HTMLDivElement>) =>
                this.onMouseEnterOrFocus(item, ev)
              } // eslint-disable-line react/jsx-no-bind
              // tslint:disable-next-line:jsx-no-lambda
              onClick={(ev: React.MouseEvent<HTMLDivElement>) =>
                this.onClick(item, ev)
              } // eslint-disable-line react/jsx-no-bind
              onMouseLeave={this.onMouseLeave}
              onWheel={this.onWheel}
              // tslint:disable-next-line:jsx-no-lambda
              onFocus={() => this.onMouseEnterOrFocus(item)} // eslint-disable-line react/jsx-no-bind
              // tslint:disable-next-line:jsx-no-lambda
              onMouseMove={(ev: React.MouseEvent<HTMLDivElement>) =>
                this.onMouseMove(item, ev)
              } // eslint-disable-line react/jsx-no-bind
              // tslint:disable-next-line:jsx-no-lambda
              onKeyDown={(ev: React.KeyboardEvent<HTMLDivElement>) =>
                this.onKeydown(item, ev)
              } // eslint-disable-line react/jsx-no-bind
              data-is-focusable={true}
              tabIndex={0}
            />
          );
        }}
      </AppThemeConsumer>
    );
  };

  private onMouseEnterOrFocus = (
    item: InsertTableCell,
    ev?: React.MouseEvent<HTMLDivElement>
  ) => {
    if (!this.isNavigationIdle || this.props.disabled) {
      return;
    }

    if (this.props.onPreviewTitle) {
      this.updatePreviewTitle(
        item.index,
        this.props.columns,
        this.cellCount,
        true /* updateDisplayTitle */
      );
    }

    // If we got a mouseEvent call focus on the currentTarget
    // since we do not pass an event as part of onFocus
    ev && ev.currentTarget.focus();

    this.setState({
      hoveredCellIndex: item.index,
    });
  };

  private onClick = (
    item: InsertTableCell,
    ev: React.KeyboardEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>
  ) => {
    if (this.props.disabled) {
      return;
    }

    if (this.props.onExecute) {
      const column = columnIndexForCellAtIndex(
        item.index,
        this.props.columns,
        this.cellCount
      );
      const row = rowIndexForCellAtIndex(
        item.index,
        this.props.columns,
        this.cellCount
      );
      if (column !== undefined && row !== undefined) {
        const { id, location } = this.props;
        this.props.onExecute({
          id,
          columns: column + 1,
          rows: row + 1,
          location,
          triggerTime: ev.timeStamp,
          inputMethod: getInputMethodFromEvent(ev),
        });
      }
    }
  };

  private onMouseMove = (
    item: InsertTableCell,
    ev: React.MouseEvent<HTMLDivElement>
  ): void => {
    const targetElement = ev.currentTarget as HTMLElement;

    // If navigation is not idle or the targetElement is the focused element bail out
    if (
      !this.isNavigationIdle ||
      (document && targetElement === (document.activeElement as HTMLElement))
    ) {
      return;
    }

    if (this.props.onPreviewTitle) {
      this.updatePreviewTitle(
        item.index,
        this.props.columns,
        this.cellCount,
        true /* updateDisplayTitle */
      );
    }

    ev.currentTarget.focus();

    this.setState({
      hoveredCellIndex: item.index,
    });
  };

  private generateItems = (numberOfItems: number): InsertTableCell[] => {
    let index = 0;
    const items: InsertTableCell[] = [];
    while (index < numberOfItems) {
      items.push(this.generateItem(index));
      index += 1;
    }

    return items;
  };

  private generateItem(index: number): InsertTableCell {
    return {
      index,
      label: this.updatePreviewTitle(
        index,
        this.props.columns,
        this.cellCount,
        false /* updateDisplayTitle */
      ),
    };
  }

  private isCellHighlighted = (index: number): boolean => {
    if (this.state.hoveredCellIndex === undefined) {
      return false;
    }

    return (
      index <= this.state.hoveredCellIndex &&
      index % this.props.columns <=
        this.state.hoveredCellIndex % this.props.columns
    );
  };

  private onWheel = () => {
    this.setNavigationTimeout();
  };

  /**
   * Sets a timeout so we won't process any mouse "hover" events
   * while navigating (via mouseWheel or arrowKeys)
   */
  private setNavigationTimeout = () => {
    if (!this.isNavigationIdle && this.navigationIdleTimeoutId !== undefined) {
      this.async.clearTimeout(this.navigationIdleTimeoutId);
      this.navigationIdleTimeoutId = undefined;
    } else {
      this.isNavigationIdle = false;
    }

    this.navigationIdleTimeoutId = this.async.setTimeout(() => {
      this.isNavigationIdle = true;
    }, this.navigationIdleDelay_ms);
  };
}

export function columnIndexForCellAtIndex(
  index: number,
  columnCount: number,
  cellCount: number
): number | undefined {
  if (!isValidIndex(index, cellCount)) {
    return undefined;
  }
  return index % columnCount;
}

export function rowIndexForCellAtIndex(
  index: number,
  columnCount: number,
  cellCount: number
): number | undefined {
  if (!isValidIndex(index, cellCount)) {
    return undefined;
  }
  return Math.floor(index / columnCount);
}

export function isValidIndex(index: number, cellCount: number): boolean {
  return index < cellCount && index >= 0;
}

export function indexForKeyboardEvent(
  key: number,
  currentlyHoveredCellIndex: number | undefined,
  numberOfColumns: number,
  numberOfCells: number
): number {
  if (currentlyHoveredCellIndex === undefined) {
    return 0;
  }

  let newHoveredCellIndex = 0;

  switch (key) {
    case 38: {
      // up
      if (currentlyHoveredCellIndex < numberOfColumns) {
        newHoveredCellIndex =
          numberOfCells - (numberOfColumns - currentlyHoveredCellIndex);
      } else {
        newHoveredCellIndex = currentlyHoveredCellIndex - numberOfColumns;
      }
      break;
    }
    case 40: {
      // down
      if (currentlyHoveredCellIndex >= numberOfCells - numberOfColumns) {
        newHoveredCellIndex =
          currentlyHoveredCellIndex - (numberOfCells - numberOfColumns);
      } else {
        newHoveredCellIndex = currentlyHoveredCellIndex + numberOfColumns;
      }
      break;
    }
    case 37: {
      // left
      if (isIndexOnFirstColumn(currentlyHoveredCellIndex, numberOfColumns)) {
        newHoveredCellIndex = currentlyHoveredCellIndex + numberOfColumns - 1;
      } else {
        newHoveredCellIndex = currentlyHoveredCellIndex - 1;
      }
      break;
    }
    case 39: {
      // right
      if (isIndexOnLastColumn(currentlyHoveredCellIndex, numberOfColumns)) {
        newHoveredCellIndex = currentlyHoveredCellIndex - numberOfColumns + 1;
      } else {
        newHoveredCellIndex = currentlyHoveredCellIndex + 1;
      }
      break;
    }
  }
  return newHoveredCellIndex;
}

function isIndexOnFirstColumn(index: number, numberOfColumns: number): boolean {
  return index % numberOfColumns === 0;
}

function isIndexOnLastColumn(index: number, numberOfColumns: number): boolean {
  return index % numberOfColumns === numberOfColumns - 1;
}
